import React, { useEffect, useState } from "react";
import { Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { ClinicalBanner } from "@/components/Disclaimer";

export default function PharmaManager() {
  const [visits, setVisits] = useState([]);
  const [sun, setSun] = useState(null);
  const [samples, setSamples] = useState([]);
  const [formulary, setFormulary] = useState([]);
  const [form, setForm] = useState({ rep_name: "", company: "", drug_discussed: "", samples_left: 0, notes: "" });

  const load = () => {
    api.get("/pharma/rep-visits").then(r=>setVisits(r.data));
    api.get("/pharma/sunshine-act").then(r=>setSun(r.data));
    api.get("/pharma/samples-inventory").then(r=>setSamples(r.data));
    api.get("/pharma/formulary").then(r=>setFormulary(r.data));
  };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/pharma/rep-visits", form);
    toast.success("Visit logged");
    setForm({ rep_name: "", company: "", drug_discussed: "", samples_left: 0, notes: "" });
    load();
  };

  const visitCols = [
    { key: "rep_name", label: "Rep" },
    { key: "company", label: "Company" },
    { key: "drug_discussed", label: "Drug" },
    { key: "samples_left", label: "Samples" },
    { key: "visit_date", label: "Visit", render: (r)=>new Date(r.visit_date).toLocaleDateString() },
  ];

  const sampleCols = [
    { key: "drug", label: "Drug" },
    { key: "qty", label: "Qty" },
    { key: "expires", label: "Expires", render: (r)=><span className={r.alert?"text-red-600 font-semibold":""}>{r.expires} {r.alert && <AlertTriangle className="w-3.5 h-3.5 inline ml-1" />}</span> },
  ];

  return (
    <>
      <PageHeader eyebrow="Module 17" title="Pharma & Drug Relationship Manager"
        subtitle="Rep visits, Sunshine Act compliance, sample inventory and payer formulary intelligence." testid="pharma-page" />
      <ClinicalBanner />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <SectionCard title="Log Rep Visit" testid="rep-form">
          <form onSubmit={submit} className="space-y-2">
            <input placeholder="Rep name" value={form.rep_name} onChange={(e)=>setForm({...form, rep_name:e.target.value})} required className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" />
            <input placeholder="Company" value={form.company} onChange={(e)=>setForm({...form, company:e.target.value})} required className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" />
            <input placeholder="Drug discussed" value={form.drug_discussed} onChange={(e)=>setForm({...form, drug_discussed:e.target.value})} required className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" />
            <input type="number" placeholder="Samples left" value={form.samples_left} onChange={(e)=>setForm({...form, samples_left:Number(e.target.value)})} className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" />
            <button type="submit" className="w-full bg-brand-primary text-white py-2 rounded-md text-sm font-medium hover:bg-[#35096B]"><Plus className="w-4 h-4 inline mr-1" />Log Visit</button>
          </form>
        </SectionCard>
        <div className="lg:col-span-2">
          <SectionCard title="Recent Visits" testid="rep-visits">
            <DataTable data={visits} columns={visitCols} searchKeys={["rep_name","drug_discussed","company"]} testid="rep-table" />
          </SectionCard>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Sunshine Act (YTD)" testid="sunshine">
          {sun && (
            <>
              <div className="text-3xl font-semibold text-brand-primary">${sun.ytd_total.toLocaleString()}</div>
              <div className="text-xs text-brand-muted mb-3">Filing due {sun.filing_due}</div>
              <div className="space-y-1.5 text-sm">
                {sun.items.map((it,i)=>(
                  <div key={i} className="flex justify-between border-b border-brand pb-1.5">
                    <span>{it.date} • {it.company} ({it.type})</span>
                    <span className="font-mono">${it.value.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </SectionCard>
        <SectionCard title="Sample Inventory" testid="samples">
          <DataTable data={samples} columns={sampleCols} searchKeys={["drug"]} testid="samples-table" />
        </SectionCard>
      </div>

      <div className="mt-6">
        <SectionCard title="Payer Formulary" subtitle="Coverage tiers by payer" testid="formulary">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead><tr className="text-left"><th className="px-3 py-2 text-xs uppercase tracking-wider text-brand-muted">Drug</th>{["BlueCross","Aetna","United","Medicare"].map(p=><th key={p} className="px-3 py-2 text-xs uppercase tracking-wider text-brand-muted">{p}</th>)}</tr></thead>
              <tbody>{formulary.map((f,i)=>(<tr key={i} className="border-t border-brand"><td className="px-3 py-2 font-medium">{f.drug}</td>{["BlueCross","Aetna","United","Medicare"].map(p=><td key={p} className="px-3 py-2 text-brand-muted">{f[p]}</td>)}</tr>))}</tbody>
            </table>
          </div>
        </SectionCard>
      </div>
    </>
  );
}
